<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>
            <h3>{{ pageTitle }}</h3>
            <b-table
                responsive
                small
                :items="filteredItems"
                :fields="fields"
                :filter="filter"
                :filter-included-fields="['search']"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                primary-key="client_id"
                @filtered="onFiltered"
            >
                <template #cell(id)="data">
                    <div class="dropdown">
                        <button
                            id="dropdownMenuButton"
                            class="btn p-0 dropdown-toggle"
                            type="button"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <i class="far fa-bars"></i>
                        </button>
                        <div
                            class="dropdown-menu"
                            aria-labelledby="dropdownMenuButton"
                        >
                            <b-link
                                v-if="showByCompany"
                                class="dropdown-item"
                                @click="
                                    byCompany(data.item.id, data.item.title)
                                "
                            >
                                <i class="fas fa-caret-right fa-fw"></i>
                                {{ data.item.title }} par cabinet
                            </b-link>
                            <b-link
                                v-if="showByActivity"
                                class="dropdown-item"
                                @click="
                                    byActivity(data.item.id, data.item.title)
                                "
                            >
                                <i class="fas fa-caret-right fa-fw"></i>
                                {{ data.item.title }} par activité
                            </b-link>
                            <b-link
                                v-if="showByClient"
                                class="dropdown-item"
                                @click="byClient(data.item.id, data.item.title)"
                            >
                                <i class="fas fa-caret-right fa-fw"></i>
                                {{ data.item.title }} par client
                            </b-link>
                        </div>
                    </div>
                </template>
                <template #cell(jan)="data">
                    <span :class="cellClasses[1]">{{
                        formatNumber(data.item.jan)
                    }}</span>
                </template>
                <template #cell(feb)="data">
                    <span :class="cellClasses[2]">{{
                        formatNumber(data.item.feb)
                    }}</span>
                </template>
                <template #cell(mar)="data">
                    <span :class="cellClasses[3]">{{
                        formatNumber(data.item.mar)
                    }}</span>
                </template>
                <template #cell(apr)="data">
                    <span :class="cellClasses[4]">{{
                        formatNumber(data.item.apr)
                    }}</span>
                </template>
                <template #cell(may)="data">
                    <span :class="cellClasses[5]">{{
                        formatNumber(data.item.may)
                    }}</span>
                </template>
                <template #cell(jun)="data">
                    <span :class="cellClasses[6]">{{
                        formatNumber(data.item.jun)
                    }}</span>
                </template>
                <template #cell(jul)="data">
                    <span :class="cellClasses[7]">{{
                        formatNumber(data.item.jul)
                    }}</span>
                </template>
                <template #cell(aug)="data">
                    <span :class="cellClasses[8]">{{
                        formatNumber(data.item.aug)
                    }}</span>
                </template>
                <template #cell(sep)="data">
                    <span :class="cellClasses[9]">{{
                        formatNumber(data.item.sep)
                    }}</span>
                </template>
                <template #cell(oct)="data">
                    <span :class="cellClasses[10]">{{
                        formatNumber(data.item.oct)
                    }}</span>
                </template>
                <template #cell(nov)="data">
                    <span :class="cellClasses[11]">{{
                        formatNumber(data.item.nov)
                    }}</span>
                </template>
                <template #cell(dec)="data">
                    <span :class="cellClasses[12]">
                        {{ formatNumber(data.item.dec) }}</span
                    >
                </template>
                <template #cell(year)="data">
                    {{ formatNumber(data.item.year) }}
                </template>
                <!-- FOOT -->
                <template #custom-foot="data">
                    <b-tr class="bg-light">
                        <b-th></b-th>
                        <b-th>TOTAL</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.jan)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.feb)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.mar)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.apr)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.may)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.jun)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.jul)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.aug)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.sep)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.oct)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.nov)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.dec)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.year)
                        }}</b-th>
                    </b-tr>
                </template>
            </b-table>
            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    ></b-pagination>
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from '../../../common/components/loading'
import TableHandler from '../../../common/mixins/TableHandler'
import DashboardHandler from '../../mixins/DashboardHandler'
import monthChargeHandler from '../../../common/components/MonthChargeHandler.vue'

export default {
    name: 'BudgetRollingInteractive',

    components: {
        loading,
    },

    mixins: [TableHandler, DashboardHandler],

    data() {
        return {
            fields: [
                { key: 'id', label: '', class: 'td-icon', sortable: false },
                { key: 'title', label: '', sortable: true },
                {
                    key: 'jan',
                    label: 'jan',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'feb',
                    label: 'fév',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'mar',
                    label: 'mar',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'apr',
                    label: 'avr',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'may',
                    label: 'mai',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'jun',
                    label: 'jun',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'jul',
                    label: 'jul',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'aug',
                    label: 'aoû',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'sep',
                    label: 'sep',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'oct',
                    label: 'oct',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'nov',
                    label: 'nov',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'dec',
                    label: 'déc',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
                {
                    key: 'year',
                    label: 'TOTAL',
                    class: 'text-right td-dashboard-data-sm',
                    sortable: true,
                },
            ],
            total: {},
            dataUrl: '',
            showByClient: true,
            showByActivity: true,
            showByCompany: true,
            filterType: 'all',
            filterId: '',
            pageTitle: 'Global',
            cellClasses: [],
        }
    },

    computed: {
        year() {
            return new Date().getFullYear()
        },
    },

    async created() {
        this.dataUrl = 'dashboard/rolling-budget/global/'
        this.setCellClasses()
        await this.getData()
    },

    methods: {
        setCellClasses() {
            const today = new Date()
            const month = today.getMonth() + 1
            for (let month = today.getMonth() + 1; month <= 12; month++) {
                this.cellClasses[month] = 'font-italic'
            }
            // this.cellClasses[today.getMonth()] = 'font-italic border-right border-dark pr-2'
        },

        async byCompany(id, title) {
            this.showByClient = true
            this.showByActivity = true
            this.showByCompany = false
            this.dataUrl = 'dashboard/rolling-budget/by-company/'
            this.filterId = id
            await this.getData()
            this.pageTitle = title
            this.filterType = 'company'
        },

        async byActivity(id, title) {
            this.showByClient = true
            this.showByActivity = false
            this.showByCompany = true
            this.dataUrl = 'dashboard/rolling-budget/by-activity/'
            this.filterId = id
            await this.getData()
            this.pageTitle = title
            this.filterType = 'activity'
        },

        async byClient(id, title) {
            this.showByClient = false
            this.showByActivity = true
            this.showByCompany = false
            this.filterId = id
            this.dataUrl = 'dashboard/rolling-budget/by-client/'
            await this.getData()
            this.pageTitle = title
            this.filterType = 'client'
        },

        async getData() {
            this.loading = true
            try {
                const response = await axios.get(
                    `${this.dataUrl + this.year}/${this.filterType}/${
                        this.filterId
                    }`
                )
                this.items = response.data.data
                this.calculateTotal()
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        initTotal() {
            this.total.jan = 0
            this.total.feb = 0
            this.total.mar = 0
            this.total.apr = 0
            this.total.may = 0
            this.total.jun = 0
            this.total.jul = 0
            this.total.aug = 0
            this.total.sep = 0
            this.total.oct = 0
            this.total.nov = 0
            this.total.dec = 0
            this.total.year = 0
        },

        calculateTotal() {
            this.initTotal()

            this.items.forEach((item) => (this.total.jan += item.jan))
            this.items.forEach((item) => (this.total.feb += item.feb))
            this.items.forEach((item) => (this.total.mar += item.mar))
            this.items.forEach((item) => (this.total.apr += item.apr))
            this.items.forEach((item) => (this.total.may += item.may))
            this.items.forEach((item) => (this.total.jun += item.jun))
            this.items.forEach((item) => (this.total.jul += item.jul))
            this.items.forEach((item) => (this.total.aug += item.aug))
            this.items.forEach((item) => (this.total.sep += item.sep))
            this.items.forEach((item) => (this.total.oct += item.oct))
            this.items.forEach((item) => (this.total.nov += item.nov))
            this.items.forEach((item) => (this.total.dec += item.dec))
            this.items.forEach((item) => (this.total.year += item.year))
        },

        formatNumber(number) {
            if (number === 0) {
                return '-'
            }
            if (number === null) {
                return '-'
            }
            return Math.floor(number).toLocaleString()
        },
    },
}
</script>

<style scoped></style>
